'use client';

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LogIn, Mail, Lock, BookOpen, User } from 'lucide-react';

interface LoginPageProps {
  onLogin: (email: string, password: string) => Promise<void>;
  onGuestLogin: () => void;
  isLoading?: boolean;
  error?: string;
}

export function LoginPage({ onLogin, onGuestLogin, isLoading = false, error }: LoginPageProps) {
  const [activeTab, setActiveTab] = useState<'member' | 'guest'>('member');
  const [email, setEmail] = useState('demo@studyspot.com');
  const [password, setPassword] = useState('demo1234');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (email && password) {
      await onLogin(email, password);
    }
  };

  const handleGuestSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onGuestLogin();
  };

  return (
    <div className="max-w-md mx-auto min-h-[600px] bg-gradient-to-br from-emerald-50 to-teal-100 flex flex-col">
      {/* 헤더 */}
      <div className="text-center pt-12 pb-6">
        <div className="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
          <BookOpen className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-1">StudySpot</h1>
        <p className="text-gray-600 text-sm">스터디룸 예약 시스템</p>
      </div>

      {/* 탭 */}
      <div className="px-6">
        <div className="flex bg-white rounded-xl p-1 shadow-sm">
          <button
            onClick={() => setActiveTab('member')}
            className={`flex-1 py-3 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'member'
                ? 'bg-emerald-500 text-white shadow-md'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            회원 로그인
          </button>
          <button
            onClick={() => setActiveTab('guest')}
            className={`flex-1 py-3 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'guest'
                ? 'bg-emerald-500 text-white shadow-md'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            비회원 이용
          </button>
        </div>
      </div>

      {/* 컨텐츠 */}
      <div className="flex-1 px-6 py-6">
        <div className="bg-white rounded-2xl shadow-lg p-6">
          {/* 에러 메시지 */}
          {error && activeTab === 'member' && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {activeTab === 'member' ? (
            /* 회원 로그인 폼 */
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-gray-500" />
                  이메일
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="이메일을 입력하세요"
                  required
                  className="h-12 bg-gray-50 border-gray-200"
                  disabled={isLoading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="flex items-center gap-2 text-sm">
                  <Lock className="w-4 h-4 text-gray-500" />
                  비밀번호
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="비밀번호를 입력하세요"
                  required
                  className="h-12 bg-gray-50 border-gray-200"
                  disabled={isLoading}
                />
              </div>

              <Button
                type="submit"
                className="w-full h-12 bg-emerald-500 hover:bg-emerald-600 mt-2"
                disabled={isLoading || !email || !password}
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    로그인 중...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <LogIn className="w-4 h-4" />
                    로그인
                  </div>
                )}
              </Button>

              <p className="text-xs text-gray-500 text-center mt-4">
                데모 계정이 미리 입력되어 있습니다
              </p>
            </form>
          ) : (
            /* 비회원 이용 폼 */
            <form onSubmit={handleGuestSubmit} className="space-y-5">
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <User className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="font-medium text-gray-900 mb-2">비회원으로 이용하기</h3>
                <p className="text-sm text-gray-500">
                  로그인 없이 좌석 현황을 확인하고<br />
                  예약할 수 있습니다.
                </p>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  비회원 예약은 당일만 가능하며,<br />
                  예약 내역 확인이 제한됩니다.
                </p>
              </div>

              <Button
                type="submit"
                className="w-full h-12 bg-gray-800 hover:bg-gray-900"
              >
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  비회원으로 시작하기
                </div>
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
